<script lang="ts">
    import { formatDateOnly, formatMoney, formatTimeOnly, formatDateTime } from "@frontend/Pages/Formats";
    import { ApiResult, ApiResultOfWalletTransaction, User, CampaignsDTO } from "@frontend/walletapi";
    import { service } from "@frontend/services";
    import { onDestroy, onMount } from "svelte";
    import { Timeout } from "@frontend/timeout";
    import { CashMode, CashModeString, settings } from "@frontend/settings";
    import { GetCachedToken, me } from "@frontend/user";
    import { translate, currentLanguage } from "../Translator";
    export let player: User;
    export let dismiss: () => void;
    export let type: "withdraw" | "deposit";

    let result : ApiResultOfWalletTransaction | undefined;
    let amount: number = 0;
    let promotions: CampaignsDTO[] = [];
    let isProcessing: boolean = false; // Variable para controlar el estado de procesamiento
    
    // New structure for multiple promotions
    interface SelectedPromotion {
        promotion: CampaignsDTO;
        amount: number;
    }
    let selectedPromotions: SelectedPromotion[] = [];
    
    // Para manejar montos personalizados de promociones Manual
    let manualPromoAmounts: Record<number, string> = {};

    // Variable reactiva para seguir los IDs seleccionados
    $: selectedPromoIds = new Set(selectedPromotions.map(sp => sp.promotion.Id));

    // Objeto reactivo para almacenar los montos a mostrar en la UI
    $: promotionDisplayAmounts = promotions.reduce((acc, promo) => {
        const selectedPromo = selectedPromotions.find(sp => sp.promotion.Id === promo.Id);
        let displayAmount = promo.StartAmount?.InCents || 0;

        if (selectedPromo) {
            if (promo.Type === "Recharge") {
                // Lógica dinámica para promoción de recarga basada en Settings
                displayAmount = getRechargeAmountFromSettings(promo, amount);
            } else {
                displayAmount = selectedPromo.amount;
            }
        }
        
        acc[promo.Id] = displayAmount;
        return acc;
    }, {} as Record<number, number>);

    // Función para obtener el monto de recarga basado en los Settings
    function getRechargeAmountFromSettings(promo: CampaignsDTO, depositAmount: number): number {
        if (!promo.Settings) return 0;
        
        try {
            const settings = typeof promo.Settings === 'string' ? JSON.parse(promo.Settings) : promo.Settings;
            
            if (settings.promotions && Array.isArray(settings.promotions)) {
                const rechargePromo = settings.promotions.find((p: any) => p.type === "Recharge");
                
                if (rechargePromo && rechargePromo.ranges && Array.isArray(rechargePromo.ranges)) {
                    // Buscar el rango que coincida con el monto de depósito
                    for (const range of rechargePromo.ranges) {
                        const min = range.min || 0;
                        const max = range.max === null ? Infinity : range.max;
                        
                        if (depositAmount >= min && depositAmount <= max) {
                            return range.displayAmount || 0;
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error parsing promotion settings:', error);
        }
        
        return 0; // Valor por defecto si no se encuentra configuración válida
    }

    onMount(() => {
        if (type == "withdraw")
        {
            // Usar MoneyTransaction Balance en lugar de CashableBalance
            amount = player?.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance?.InCents ?? 0;
        }
    });

    onMount(async () => {
        try {
            const token = localStorage.getItem('token') || undefined;
            const response = await service.walletapi.campaignsList({
                Token: token
            });
            if (response.Success && response.Data) {
                // Filtrar solo promociones activas de tipo Manual y Recharge
                promotions = response.Data.filter(p => p.Active && (p.Type === "Manual" || p.Type === "Recharge"));
                
                // Seleccionar automáticamente las promociones activas
                selectedPromotions = promotions.map(promo => ({
                    promotion: promo,
                    amount: promo.StartAmount?.InCents || 0
                }));
                
                // Inicializar montos personalizados para promociones Manual
                manualPromoAmounts = promotions
                    .filter(p => p.Type === "Manual")
                    .reduce((acc, p) => {
                        acc[p.Id] = formatMoney(p.StartAmount?.InCents || 0);
                        return acc;
                    }, {} as Record<number, string>);
            } else {
                promotions = [];
                selectedPromotions = [];
            }
        } catch (e) {
            promotions = [];
            selectedPromotions = [];
        }
    });

    let printing: boolean = false;
    let printingTimeout: Timeout = new Timeout(2000, () => printing = false);
    onDestroy(() => {
        printingTimeout.Dispose();
    });
    
    function appendNumber(number: number): void {
        // Convertir el monto actual de centavos a dólares para manipularlo como texto
        let currentAmountStr = (amount / 100).toString();
        
        // Si el monto es 0, simplemente agregar el número
        if (amount === 0) {
            currentAmountStr = number.toString();
        } else {
            // Agregar el número al final
            currentAmountStr += number.toString();
        }
        
        // Convertir de vuelta a centavos
        const numericValue = parseFloat(currentAmountStr);
        amount = Math.round(numericValue * 100);
        formattedAmount = formatMoney(amount);
    }

    function add(number: number): void {
        amount += number;
        formattedAmount = formatMoney(amount);
    }
    
    function insertDoubleZero() {
        // Convertir el monto actual de centavos a dólares para manipularlo como texto
        let currentAmountStr = (amount / 100).toString();
        
        // Si el monto es 0, simplemente agregar "00"
        if (amount === 0) {
            currentAmountStr = "00";
        } else {
            // Agregar "00" al final
            currentAmountStr += "00";
        }
        
        // Convertir de vuelta a centavos
        const numericValue = parseFloat(currentAmountStr);
        amount = Math.round(numericValue * 100);
        formattedAmount = formatMoney(amount);
    }
    
    function deleteLastDigit() {
        // Convertir el monto actual de centavos a dólares para manipularlo como texto
        let currentAmountStr = (amount / 100).toString();
        
        // Si el monto es 0 o tiene solo un dígito, establecer a 0
        if (amount === 0 || currentAmountStr.length <= 1) {
            amount = 0;
        } else {
            // Eliminar el último dígito
            currentAmountStr = currentAmountStr.slice(0, -1);
            
            // Convertir de vuelta a centavos
            const numericValue = parseFloat(currentAmountStr);
            amount = Math.round(numericValue * 100);
        }
        
        formattedAmount = formatMoney(amount);
    }

    function keydown(event: KeyboardEvent) {
        // No procesar teclas si está procesando
        if (isProcessing) {
            return;
        }
        
        // Solo procesar Enter para ejecutar la acción
        if (event.key === "Enter") {
            if (type === "deposit") {
                Deposit();
            } else {
                Withdraw();
            }
        }
        // No procesar otras teclas cuando se está escribiendo en el campo de texto
        // Dejar que handleMoneyInput maneje toda la entrada del campo de texto
    }

    function wholeBalance()
    {
        // Usar MoneyTransaction Balance en lugar de CashableBalance
        amount = player?.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance?.InCents ?? 0;
        formattedAmount = formatMoney(amount);
    }

    function clearAmount()
    {
        amount = 0;
        formattedAmount = formatMoney(amount);
    }
    
    async function Withdraw() {
        // Activar el estado de procesamiento
        isProcessing = true;
        
        result = await service.walletapi.withdraw({
            Token: sessionStorage.token,
            Data: {
                UserId: player.Id,
                DepositCashMode: CashModeString.Cashable,
                Amount: {
                    InCents: amount,
                },
                Campaigns: [],
                OperatorId: $me?.Id,
                Pin: undefined,
                IsKioskWithdrawal: false,
                KioskId: undefined
            },
        });

        if (result.Success)
        {
            printing = true;
            printingTimeout.Start();
        }
    }
    
    async function Deposit() {
        // Activar el estado de procesamiento
        isProcessing = true;
        
        // Recalcular el monto final de las promociones justo antes de enviar
        const finalCampaigns = selectedPromotions.map(sp => {
            let finalAmount = sp.amount;
            if (sp.promotion.Type === "Recharge") {
                // Lógica dinámica para promoción de recarga basada en Settings
                finalAmount = getRechargeAmountFromSettings(sp.promotion, amount);
            }
            return {
                Campaign: sp.promotion,
                CampaignAmount: { InCents: finalAmount }
            };
        });

        const depositData = {
            Token: sessionStorage.token,
            Data: {
                UserId: player.Id,
                DepositCashMode: CashModeString.Cashable,
                Amount: {
                    InCents: amount,
                },
                Campaigns: finalCampaigns,
                OperatorId: $me?.Id,
                Pin: undefined,
                IsKioskWithdrawal: false,
                KioskId: undefined
            }
        };

        result = await service.walletapi.deposit(depositData);

        // Log the response to console
        console.log('Deposit Response:', result);
        console.log('PromotionTransactions from backend:', result.Data?.PromotionTransactions);
        console.log('Full result.Data structure:', JSON.stringify(result.Data, null, 2));
        
        // Calculate and log promotion balance
        if (result.Data?.PromotionTransactions) {
            const promotionBalance = result.Data.PromotionTransactions.reduce((sum, promo) => sum + (promo.Balance?.InCents || 0), 0);
            console.log('Calculated Promotion Balance:', promotionBalance);
            console.log('Formatted Promotion Balance:', formatMoney({ InCents: promotionBalance }));
        }

        // Siempre mostrar el ticket después de un depósito, exitoso o no
        if (result.Success) {
            printing = true;
            printingTimeout.Start();
        } else {
            // Si hay promociones seleccionadas y el depósito falló, manejar el mensaje de error del backend
            if (selectedPromotions.length > 0) {
                // Verificar si el backend envió un número como ErrorMessage
                if (typeof result.ErrorMessage === 'number') {
                    // Convertir el número a un mensaje formateado
                    const maxAmount = formatMoney(result.ErrorMessage);
                    // result.ErrorMessage = `Está colocando más dinero del que puede. Máximo permitido: ${maxAmount}`;
                } else if (!result.ErrorMessage) {
                    // Si no hay mensaje de error, usar el mensaje por defecto
                    result.ErrorMessage = "Está colocando más dinero del que puede.";
                }
            }
        }
    }

    async function PrintTransaction()
    {
        if (!result?.Data)
        {
            return;
        }

        let printResult: ApiResult = await service.walletapi.printTransaction({
            Token: GetCachedToken(),
            Data: {
                Id: result.Data.Id
            }
        });

        if (result.Success)
        {
            printing = true;
            printingTimeout.Start();
        }
    }
    
    // Función para resetear el estado de procesamiento
    function resetProcessingState() {
        isProcessing = false;
    }
    
    // Resetear el estado de procesamiento cuando se muestre el ticket
    $: if (result?.Data) {
        resetProcessingState();
    }
    
    async function Dismiss()
    {
        // Solo permitir cerrar si no está procesando
        if (!isProcessing) {
            dismiss();
        }
    }

    function togglePromotion(promo: CampaignsDTO) {
        const existingIndex = selectedPromotions.findIndex(sp => sp.promotion.Id === promo.Id);
        
        if (existingIndex >= 0) {
            // Remove promotion
            selectedPromotions = selectedPromotions.filter(sp => sp.promotion.Id !== promo.Id);
            // Limpiar el monto personalizado si existe de forma reactiva
            if (promo.Type === "Manual" && manualPromoAmounts[promo.Id] !== undefined) {
                const { [promo.Id]: _, ...rest } = manualPromoAmounts;
                manualPromoAmounts = rest;
            }
        } else {
            // Add promotion con su valor inicial. El cálculo dinámico se maneja reactivamente.
            const promoAmount = promo.StartAmount?.InCents || 0;
            selectedPromotions = [...selectedPromotions, {
                promotion: promo,
                amount: promoAmount
            }];
            
            // Si es promoción Manual, inicializar el monto personalizado de forma reactiva
            if (promo.Type === "Manual") {
                manualPromoAmounts = { ...manualPromoAmounts, [promo.Id]: formatMoney(promoAmount) };
            }
        }
    }

    function handleManualPromoInput(event: Event, promoId: number) {
        const input = event.target as HTMLInputElement;
        const formattedAmount = input.value;
        
        // Actualizar el valor del input de forma reactiva
        manualPromoAmounts = { ...manualPromoAmounts, [promoId]: formattedAmount };
        
        // Convertir el monto formateado a centavos y actualizar la promoción seleccionada
        const numericValue = parseFloat(formattedAmount.replace(/[^0-9.]/g, ''));
        const cents = !isNaN(numericValue) ? Math.round(numericValue * 100) : 0;
        
        selectedPromotions = selectedPromotions.map(sp => 
            sp.promotion.Id === promoId 
                ? { ...sp, amount: cents }
                : sp
        );
    }

    // Calculate total promo amount
    $: totalPromoAmount = selectedPromotions.reduce((total, sp) => {
        let currentAmount = sp.amount;
        if (sp.promotion.Type === 'Recharge') {
            // Lógica dinámica para promoción de recarga basada en Settings
            currentAmount = getRechargeAmountFromSettings(sp.promotion, amount);
        }
        return total + currentAmount;
    }, 0);

    // Función para verificar si el jugador tiene promociones activas
    function hasActivePromotions(): boolean {
        if (!player?.LastWalletTransaction?.PromotionTransactions) {
            return false;
        }
        
        const totalPromoBalance = player.LastWalletTransaction.PromotionTransactions.reduce((sum, promo) => {
            return sum + (promo.Balance?.InCents || 0);
        }, 0);
        
        return totalPromoBalance > 0;
    }

    // Función para obtener la fecha de expiración más próxima
    function getNearestExpirationDate(): Date | undefined {
        if (!player?.LastWalletTransaction?.PromotionTransactions) {
            return undefined;
        }
        
        const activePromotions = player.LastWalletTransaction.PromotionTransactions.filter(promo => 
            (promo.Balance?.InCents || 0) > 0
        );
        
        if (activePromotions.length === 0) {
            return undefined;
        }
        
        // Encontrar la fecha de expiración más próxima
        const nearestExpiration = activePromotions.reduce((nearest, promo) => {
            const promoExpiration = new Date(promo.ExpirationDate);
            return !nearest || promoExpiration < nearest ? promoExpiration : nearest;
        }, undefined as Date | undefined);
        
        return nearestExpiration;
    }

    let formattedAmount: string = formatMoney(amount);
    $: formattedAmount = formatMoney(amount);
    $: formattedTotalPromoAmount = formatMoney(totalPromoAmount);
    $: nearestExpiration = getNearestExpirationDate();


function handlePromoAmountInput(event: Event, promoId: number) {
	const input = event.target as HTMLInputElement;
	const raw = input.value;

	const numericValue = parseFloat(raw.replace(/[^0-9.]/g, ''));
	if (!isNaN(numericValue)) {
		const cents = Math.round(numericValue * 100);
		updatePromotionAmount(promoId, cents);
	} else {
		updatePromotionAmount(promoId, 0);
	}
}

function updatePromotionAmount(promoId: number, newAmount: number) {
    selectedPromotions = selectedPromotions.map(sp => 
        sp.promotion.Id === promoId 
            ? { ...sp, amount: newAmount }
            : sp
    );
}

function removePromotion(promoId: number) {
    selectedPromotions = selectedPromotions.filter(sp => sp.promotion.Id !== promoId);
}

function handleInputClick(event: Event) {
    const input = event.target as HTMLInputElement;
    // Seleccionar todo el texto cuando se hace clic en el campo
    input.select();
}

function handleInputFocus(event: Event) {
    const input = event.target as HTMLInputElement;
    // Seleccionar todo el texto cuando el campo recibe el foco
    input.select();
}

function handleAmountInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    
    // Limpiar el valor de entrada, manteniendo solo números y un punto decimal
    const cleanValue = raw.replace(/[^0-9.]/g, '');
    
    // Si el campo está vacío o solo tiene ".", establecer a 0
    if (cleanValue === '' || cleanValue === '.') {
        amount = 0;
        formattedAmount = formatMoney(0);
        return;
    }
    
    // Verificar que no haya múltiples puntos decimales
    const dotCount = (cleanValue.match(/\./g) || []).length;
    if (dotCount > 1) {
        // Si hay múltiples puntos, mantener solo el primero
        const firstDotIndex = cleanValue.indexOf('.');
        const cleanValueFixed = cleanValue.substring(0, firstDotIndex + 1) + cleanValue.substring(firstDotIndex + 1).replace(/\./g, '');
        
        // Actualizar el input con el valor corregido
        input.value = cleanValueFixed;
        const numericValue = parseFloat(cleanValueFixed);
        
        if (!isNaN(numericValue)) {
            amount = Math.round(numericValue * 100);
            formattedAmount = formatMoney(amount);
        } else {
            amount = 0;
            formattedAmount = formatMoney(0);
        }
        return;
    }

    // Convertir el valor a número
    const numericValue = parseFloat(cleanValue);
    
    if (!isNaN(numericValue)) {
        // Si el número es válido, convertirlo a centavos
        amount = Math.round(numericValue * 100);
        formattedAmount = formatMoney(amount);
    } else {
        // Si no es un número válido, establecer a 0
        amount = 0;
        formattedAmount = formatMoney(0);
    }
}
</script>

<h2 style="text-align: center; margin-bottom: 0.6em; font-size: 1.2em;">
    {#if type == "withdraw"}
        {$translate('Withdraw')}
    {:else if type == "deposit"}
        {$translate('Deposit')}
    {/if}
</h2>
   
{#if result?.Data}
    {#if result.Success}
        <div class="ui positive message">
            <div class="header">
                {#if type == "withdraw"}
                   {$translate('Withdraw')}  {formatMoney(result.Data.CashableAmount)} {$translate('Successful')}
                {:else if type == "deposit"}
                    {#if $settings.CashMode == CashMode.Normal}
                        {$translate('Deposit')} {formatMoney(result.Data.CashableAmount)} {$translate('Successful')}
                    <!-- {:else if $settings.CashMode == CashMode.Sweeps}
                        Deposit {formatMoney(result.Data.NoncashableAmount)} successful -->
                    {/if}
                {/if}
            </div>
            {#if result.Message && result.Message.trim() !== ''}
                <p style="margin-top: 0.5em; font-weight: normal; color: #856404; background-color: #fff3cd; padding: 0.5em; border-radius: 4px; border: 1px solid #ffeaa7;">
                    <i class="exclamation triangle icon"></i>
                    {result.Message}
                </p>
            {/if}
        </div>
    {:else}
        <div class="ui negative message">
            <div class="header">
                {#if type == "withdraw"}
                {$translate('Withdraw failed')}
                {:else if type == "deposit"}
                {$translate('Deposit completed with warnings')}
                {/if}
            </div>
            <p>{result.ErrorMessage ?? "Unknown error during transaction"}</p>
        </div>
    {/if}

    <div class="ui segment" style="display: flex;">
        <table class="receit-table" style="flex-grow: 1;">
            <tr>
                <td class="receit-title">{$translate('Trans Id')}</td>
                <td class="receit-value">#{result.Data.Id}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Player Id')}</td>
                <td class="receit-value">#{result.Data.UserId}</td>
            </tr>
            <!-- <tr>
                <td class="receit-title">Cashier ID</td>
                 <td class="receit-value">#{result.Data.Shift?.UserId ?? ""}</td> 
            </tr> -->
            <tr>
                <td class="receit-title">{$translate('Trans. date')}</td>
                <td class="receit-value">{formatDateOnly(result.Data.TransactionTime)}</td>
            </tr>
            <tr>
                <td class="receit-title">{  $translate('Trans. time')}</td>
                <td class="receit-value">{formatTimeOnly(result.Data.TransactionTime)}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Trans. type')}</td>
                <td class="receit-value">{result.Data.Type}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Trans. amount')}</td>
                <td class="receit-value">
                    {#if type == "withdraw"}
                        {formatMoney(result.Data.CashableAmount)}
                    {:else if type == "deposit"}
                        {#if $settings.CashMode == CashMode.Normal}
                            {formatMoney(result.Data.CashableAmount)}
                        <!-- {:else if $settings.CashMode == CashMode.Sweeps}
                            {formatMoney(result.Data.NoncashableAmount)} -->
                        {/if}
                    {/if}
                </td>
            </tr>
            <!-- {#if $settings?.DepositCashMode == DepositCashMode.Noncashable} -->
            <tr>
                <td class="receit-title">{$translate('Promotion Balance')}</td>
                <td class="receit-value">
                    {formatMoney({
                        InCents: (result.Data.PromotionTransactions?.reduce((sum, promo) => sum + (promo.Balance?.InCents || 0), 0) || 0)
                    })}
                </td>
            </tr>
            <!-- {/if} -->
            <tr>
                <td class="receit-title">{$settings.CashableName} {$translate('Balance')}</td>
                <td class="receit-value">{formatMoney(result.Data.MoneyTransaction?.[0]?.Balance || 0)}</td>
            </tr>
        </table>
    </div>

    <div style="display: flex; justify-content: center;">
        <button class="ui primary fluid button" on:click={Dismiss}>{$translate('Dismiss')}</button>
        {#if printing}
            <button class="ui fluid disabled button"><i class="loading spinner icon"></i>Printing</button>
        {:else}
            <button class="ui fluid button" on:click={PrintTransaction}>{$translate('Print')}</button>
        {/if}
    </div>


{:else}

    <table class="ui very basic very compact unstackable table player-info-table">
        <tr>
            <td>{$translate('Player Id')}</td>
            <td style="text-align: right">#{player.Id}</td>
        </tr>
        <tr>
            <td>{$translate('First Name')}</td>
            <td style="text-align: right">{player.FirstName ?? ""} {player.LastName ?? ""}</td>
        </tr>
        <tr>
            <td>{$translate('E-mail')}</td>
            <td style="text-align: right">{player.Email ?? ""}</td>
        </tr>
        <!-- {#if $settings?.DepositCashMode == DepositCashMode.Noncashable} -->
        <tr>
            <td>{$translate('Promo')}</td>
            <td style="text-align: right">
                {formatMoney({
                    InCents: (player?.LastWalletTransaction?.PromotionTransactions?.reduce((sum, promo) => sum + (promo.Balance?.InCents || 0), 0) || 0)
                })}
            </td>
        </tr>
        <!-- {/if} -->
        <tr>
            <td>{$translate('Money Balance')}</td>
            <td style="text-align: right">{formatMoney(player?.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance || { InCents: 0 })}</td>
        </tr>
    </table>
    
    <div class="ui form">
        {#if result && !result.Success}
            <div class="ui negative message">
                <!-- <div class="header"></div> -->
                {result.ErrorMessage ?? "Unknown error during transaction"}
            </div>
        {/if}

        <!-- <div class="field">
            <div class="ui labeled input" style="text-align: right;" on:click={wholeBalance()}>
                <div class="ui label">Balance</div>
                <input type="text" style="text-align: right" readonly value={formatMoney(balance)}>
            </div>
        </div> -->

        <div class="field">
            <div class="ui labeled action input" style="text-align: right;">
                <div class="ui label">{$translate('Amount')}</div>
                    <input
                        type="text"
                        class="amount-input"
                        value={formattedAmount}
                        on:input={handleAmountInput}
                        on:click={handleInputClick}
                        on:focus={handleInputFocus}
                        on:keydown={keydown}
                        placeholder="Amount (e.g. $12.34)"
                        disabled={isProcessing}
                    />
                <button 
                    class="ui icon button" 
                    on:click={clearAmount}
                    disabled={isProcessing}
                >
                    <i class="close icon"></i>
                </button>
            </div>
        </div>

        {#if type === "deposit" && (promotions.length > 0 || hasActivePromotions())}
            <div class="field">
                <div class="ui segment promo-compact" class:has-active-promotions={hasActivePromotions()}>
                    {#if hasActivePromotions()}
                        <h4 class:red-text={hasActivePromotions()}>⚠️ {$translate('Cannot apply promotions - Player has active promotions')}</h4>
                        <div class="promos-list">
                            <div class="promo-item selected red-background">
                                <span class="promo-title">{$translate('Player has active promotional balance')}</span>
                                <span class="promo-amount">{formatMoney({
                                    InCents: (player?.LastWalletTransaction?.PromotionTransactions?.reduce((sum, promo) => sum + (promo.Balance?.InCents || 0), 0) || 0)
                                })}</span>
                            </div>
                            {#if getNearestExpirationDate()}
                                <div class="promo-item selected red-background expiration-info">
                                    <span class="promo-title">
                                        <i class="calendar minus icon"></i>
                                        {$translate('Expiration Date')}
                                    </span>
                                    <span class="promo-amount expiration-date">
                                        {#if nearestExpiration}
                                            {formatDateTime(nearestExpiration)}
                                        {:else}
                                            N/A
                                        {/if}
                                    </span>
                                </div>
                            {/if}
                        </div>
                    {:else}
                        <h4>{$translate('Available Promotions')}</h4>
                        <div class="promos-list">
                            {#each promotions as promo}
                                <div class="promo-item selected">
                                    <span class="promo-title">{promo.PromotionName}</span>
                                    {#if promo.Type === "Manual"}
                                        <div class="promo-custom-amount">
                                            <input
                                                type="text"
                                                value={manualPromoAmounts[promo.Id] || ''}
                                                on:input={(e) => handleManualPromoInput(e, promo.Id)}
                                                placeholder="Amount"
                                                class="ui mini input"
                                                style="width: 80px; text-align: right;"
                                            />
                                        </div>
                                    {:else}
                                        <span class="promo-amount">{formatMoney(promotionDisplayAmounts[promo.Id] || 0)}</span>
                                    {/if}
                                </div>
                            {/each}
                        </div>
                    {/if}
                </div>
            </div>
        {/if}

        <div class="field">
            <div class="transaction-actions">
                <div class="field">
                    <button 
                        class="ui negative fluid button" 
                        class:disabled={isProcessing}
                        on:click={Dismiss}
                        disabled={isProcessing}
                    >
                        {#if isProcessing}
                            <i class="loading spinner icon"></i>
                        {/if}
                        {$translate('Cancel')}
                    </button>
                </div>
                <div class="field">
                    {#if type == "withdraw"}
                        <button 
                            class="ui positive fluid button" 
                            class:disabled={isProcessing}
                            on:click={Withdraw}
                            disabled={isProcessing}
                        >
                            {#if isProcessing}
                                <i class="loading spinner icon"></i>
                            {/if}
                            {$translate('Withdraw')}
                        </button>
                    {:else if type == "deposit"}
                        <button 
                            class="ui positive fluid button" 
                            class:disabled={isProcessing}
                            on:click={Deposit}
                            disabled={isProcessing}
                        >
                            {#if isProcessing}
                                <i class="loading spinner icon"></i>
                            {/if}
                            {$translate('Deposit')}
                        </button>
                    {/if}
                </div>
            </div>
        </div>
    </div>

    <div class="common-values">
        <div class="row">
            <div>
                <button 
                    class="ui fluid button" 
                    class:disabled={isProcessing}
                    on:click={() => add(100)}
                    disabled={isProcessing}
                >$1</button>
            </div>
            <div>
                <button 
                    class="ui fluid button" 
                    class:disabled={isProcessing}
                    on:click={() => add(200)}
                    disabled={isProcessing}
                >$2</button>
            </div>
            <div>
                <button 
                    class="ui fluid button" 
                    class:disabled={isProcessing}
                    on:click={() => add(500)}
                    disabled={isProcessing}
                >$5</button>
            </div>
            <div>
                <button 
                    class="ui fluid button" 
                    class:disabled={isProcessing}
                    on:click={() => add(1000)}
                    disabled={isProcessing}
                >$10</button>
            </div>
        </div>

        <div class="row">
            <div>
                <button 
                    class="ui fluid button" 
                    class:disabled={isProcessing}
                    on:click={() => add(2000)}
                    disabled={isProcessing}
                >$20</button>
            </div>
            <div>
                <button 
                    class="ui fluid button" 
                    class:disabled={isProcessing}
                    on:click={() => add(5000)}
                    disabled={isProcessing}
                >$50</button>
            </div>
            <div>
                <button 
                    class="ui fluid button" 
                    class:disabled={isProcessing}
                    on:click={() => add(10000)}
                    disabled={isProcessing}
                >$100</button>
            </div>
        </div>
    </div>

    <table style="margin: auto">
        <thead>
            <tr>
                <td colspan="3">
                    <!-- {#if usePromo}
                        <div class="ui label">Promo Amount: {formatMoney(totalPromoAmount)}</div>
                    {:else}
                        <div class="ui label">Amount: {formatMoney(amount)}</div>
                    {/if} -->
                </td>
            </tr>
            <tr>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(1)} disabled={isProcessing}>1</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(2)} disabled={isProcessing}>2</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(3)} disabled={isProcessing}>3</button></td>
            </tr>
            <tr>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(4)} disabled={isProcessing}>4</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(5)} disabled={isProcessing}>5</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(6)} disabled={isProcessing}>6</button></td>
            </tr>
            <tr>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(7)} disabled={isProcessing}>7</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(8)} disabled={isProcessing}>8</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(9)} disabled={isProcessing}>9</button></td>
            </tr>
            <tr>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={() => appendNumber(0)} disabled={isProcessing}>0</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={insertDoubleZero} disabled={isProcessing}>00</button></td>
                <td><button class="ui button keypad-button" class:disabled={isProcessing} on:click={deleteLastDigit} disabled={isProcessing}><i class="arrow alternate circle left icon" /></button></td>
            </tr>
        </thead>
    </table>

{/if}

<style>
    .transaction-actions {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 0.75rem;
    }

    .ui.form .transaction-actions > .field {
        margin: 0;
        min-width: 0;
    }

    .ui.button.keypad-button {
        width: 60px;
        height: 28px;
        text-align: center;
        font-size: 0.9em;
    }

    .receit-table
    {
        border-spacing: 0px;
        font-family: 'Courier New', Courier, monospace;
    }

    .receit-table td
    {
        padding-top: 5px;
    }

    .receit-table td:first-child
    {
        margin-right: 0.2em;
    }

    .receit-title
    {
        text-align: left;
        border-bottom: 1px solid black;
        padding-right: 10px;
    }

    .receit-value
    {
        text-align: right;
        border-bottom: 1px solid black;
    }

    .common-values .row
    {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .common-values .row > div
    {
        flex-grow: 1;
        margin: 0.05rem;
    }
    
    .common-values .row > div button {
        font-size: 0.8em;
        padding: 0.3em 0.1em;
        min-height: 28px;
    }

    .promos-list {
        padding: 2px;
        font-size: 12px;
    }
    .promo-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 2px 4px;
        margin-bottom: 2px;
        border-radius: 3px;
        background: #f0f0f0;
        transition: background 0.2s;
    }
    .promo-title {
        font-weight: 500;
        font-size: 12px;
        flex-grow: 1;
    }
    .promo-amount {
        font-size: 12px;
        color: #333;
        margin-left: 8px;
    }
    
    .promo-custom-amount {
        margin-left: 8px;
    }
    
    .promo-custom-amount input {
        font-size: 11px;
        padding: 2px 4px;
        border: 1px solid #ccc;
        border-radius: 3px;
    }
    
    .promo-custom-amount input:focus {
        border-color: #007bff;
        outline: none;
    }
    
    .player-info-table {
        font-size: 0.85em;
    }
    
    .player-info-table td {
        font-size: 0.8em;
        padding: 0.05em 0.3em;
    }
    
    .player-info-table tr {
        line-height: 1.1;
    }

    .promo-compact {
        font-size: 0.75em;
        padding: 0.5em 0.5em 0.3em 0.5em !important;
    }
    .promo-compact .promos-list {
        padding: 0;
    }
    .promo-compact .promo-item {
        padding: 1px 1px;
        margin-bottom: 0.5px;
        min-height: 18px;
    }
    .promo-compact .promo-title {
        font-size: 0.85em;
    }
    .promo-compact .promo-amount {
        font-size: 0.85em;
        margin-left: 4px;
    }
    .promo-compact .promo-custom-amount input {
        font-size: 0.85em;
        padding: 0 2px;
        height: 16px;
        width: 60px !important;
    }
    
    /* Estilos para hacer el modal más compacto */
    .ui.form .field {
        margin-bottom: 0.6em;
    }
    
    .ui.segment {
        padding: 0.6em;
        margin-bottom: 0.6em;
    }
    
    .ui.message {
        padding: 0.6em;
        margin-bottom: 0.6em;
    }
    
    .ui.button {
        padding: 0.5em 0.8em;
    }
    
    .ui.fluid.button {
        padding: 0.5em 0.6em;
    }
    
    h2 {
        margin-bottom: 0.6em;
        font-size: 1.1em;
    }
    
    .player-info-table {
        margin-bottom: 0.6em;
    }
    
    .common-values {
        margin-bottom: 0.6em;
    }
    
    table {
        margin-bottom: 0.6em;
    }
    
    /* Reducir padding general del modal */
    .ui.form {
        padding: 0.5em;
    }
    
    .ui.segment {
        margin: 0.5em 0;
    }
    
    /* Estilos para resaltar promociones activas en rojo */
    .has-active-promotions {
        border: 2px solid #db2828 !important;
        background-color: #fff5f5 !important;
    }
    
    .red-text {
        color: #db2828 !important;
        font-weight: bold !important;
    }
    
    .red-background {
        background-color: #ffebee !important;
        border: 1px solid #ffcdd2 !important;
    }
    
    .red-background .promo-title {
        color: #c62828 !important;
        font-weight: bold !important;
        font-size: 0.75em !important;
    }
    
    .red-background .promo-amount {
        color: #c62828 !important;
        font-weight: bold !important;
        font-size: 0.75em !important;
    }
    
    .expiration-info {
        border-top: 1px solid #ffcdd2 !important;
        margin-top: 2px !important;
        padding-top: 4px !important;
    }
    
    .expiration-date {
        font-size: 0.7em !important;
        color: #d32f2f !important;
        font-weight: bold !important;
        white-space: nowrap !important;
        text-align: right !important;
    }
    
    .red-text {
        font-size: 0.9em !important;
    }

    @media (max-width: 767px) {
        .ui.table.player-info-table {
            font-size: 1.275em;
        }

        .amount-input {
            min-width: 0;
            font-size: 16px !important;
        }

        .ui.button {
            min-height: 44px;
        }

        .keypad-button,
        .common-values .row > div button {
            min-height: 44px;
        }

        .ui.button.keypad-button,
        .common-values .row > div button.ui.button {
            font-size: 18px;
            font-weight: 600;
        }
    }
</style>
